<template>
  <div
    v-if="!hasComportamentosAssociados"
    style="width: 100%"
  >
    <v-row>
      <v-col>
        <app-alert
          icon="info"
          message="Não há comportamentos associados cadastrados."
          type="info"
        />
      </v-col>
    </v-row>
  </div>
  <div
    v-else
    style="width: 100%"
  >
    <!-- bar chart -->
    <v-row id="grafico-barras" no-gutters>
      <v-col>
        <bar-chart
          :categorias="dadosBarchart.categorias"
          :dados="dadosBarchart.dados"
          :fonte="`Fonte: Sistema Desenvolva, ${anoMapeamento}`"
          :tooltip="dadosBarchart.tooltip"
          class="chart"
          data-type="integer"
          descricao-eixo-y="Comportamentos Associados"
          titulo="Monitoramento do Cadastro de Competências Comportamentais"
        />
      </v-col>
    </v-row>

    <!-- pie chart -->
    <v-row id="grafico-pizza" no-gutters>
      <v-col>
        <pie-chart
          :dados="dadosPiechartAssociados.dados"
          :fonte="`Fonte: Sistema Desenvolva, ${anoMapeamento}`"
          class="chart"
          titulo="Comparativo de Competências Comportamentais"
        />
      </v-col>
    </v-row>

    <!-- Resultados -->
    <v-row>
      <v-toolbar flat>
        <v-toolbar-title>
          <h2 class="title primary--text text--darken-1 text-no-wrap mt-0">
            RESULTADOS
          </h2>
        </v-toolbar-title>
      </v-toolbar>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :headers="dadosTabela.headers"
          :items="dadosTabela.items"
          align-content-space-between
          class="elevation-0"
          disable-pagination
          hide-default-footer
        />
      </v-col>
    </v-row>
    <!-- Relatório PDF -->
    <v-row
      class="justify-end mb-1"
    >
      <v-tooltip
        max-width="400"
        top
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="success"
            @click="getRelatorio()"
            v-on="on"
          >
            <v-icon>get_app</v-icon>
            BAIXAR PDF
          </v-btn>
        </template>
        <span>Clique para baixar o relatório dos dados em formato PDF.</span>
      </v-tooltip>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState } from 'vuex';
import BarChart from '@/components/chart/BarChart';
import PieChart from '@/components/chart/PieChart';
import AppAlert from '@/components/core/AppAlert';
import { generatePDF, getCanvas } from '@/modules/relatorioPDF';

const ID_COMPETENCIA_OUTROS = 62;

export default {
  name: 'MonitoramentoUnidadeCompetenciaComportamental',

  components: { BarChart, PieChart, AppAlert },

  props: {
    mapeamento: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      visualizacaoCompetencias: 'associados',
    };
  },

  computed: {
    ...mapState({
      todasCompetenciasComportamentais: (state) => state.competencia.competenciasComportamentais,
      dadosUnidades: (state) => state.monitoramento.mapeamento.dadosUnidades,
      unidadeSelecionada: (state) => state.monitoramento.mapeamento.unidade,
    }),

    hasComportamentosAssociados() {
      return this.dadosUnidade.competenciasComportamentais.competencias.reduce((t, c) => t + c.total, 0) > 0;
    },

    dadosUnidade() {
      return this.dadosUnidades.find((u) => u.unidade.id === this.unidadeSelecionada.id);
    },

    dadosTabela() {
      return {
        headers: [
          { text: 'UNIDADE', value: 'unidade', sortable: false },
          ...this.competenciasComportamentaisOrdenadas.map((c) => ({
            text: c.nome,
            value: `${c.id}`,
            sortable: false,
          })),
          { text: 'Total de Comportamentos Associados', value: 'total', sortable: false },
        ],
        items: [
          {
            unidade: this.dadosUnidade.unidade.sigla,
            ...Object.fromEntries(
              this.competenciasComportamentaisOrdenadas.map((c) => [
                `${c.id}`,
                this.mCompetenciaAssociadoQtdComportamento.get(c.nome) || 0,
              ]),
            ),
            total: this.dadosUnidade.competenciasComportamentais.competencias.reduce((t, c) => t + c.total, 0),
          },
        ],
        itemsPDF: [[
          this.dadosUnidade.unidade.sigla,
          ...this.competenciasComportamentaisOrdenadas.map((c) => (
            this.mCompetenciaAssociadoQtdComportamento.get(c.nome) || 0
          )),
          this.dadosUnidade.competenciasComportamentais.competencias.reduce((t, c) => t + c.total, 0),
        ]],
      };
    },

    anoMapeamento() {
      return this.mapeamento.ano ? this.mapeamento.ano : '';
    },

    competenciaComportamentalOUTROS() {
      return this.todasCompetenciasComportamentais.find((c) => c.id === ID_COMPETENCIA_OUTROS);
    },

    competenciasComportamentaisOrdenadas() {
      const todasOrdenadas = this.todasCompetenciasComportamentais
        .slice()
        .filter((c) => c.id !== ID_COMPETENCIA_OUTROS)
        .sort((a, b) => a.nome.localeCompare(b.nome));

      return [...todasOrdenadas, this.competenciaComportamentalOUTROS];
    },

    mCompetenciaAssociadoQtdComportamento() {
      return this.dadosUnidade.competenciasComportamentais.competencias.reduce((m, item) => {
        m.set(item.competencia.nome, item.total);
        return m;
      }, new Map());
    },

    dadosBarchart() {
      return {
        dados: [
          {
            name: 'Competências',
            data: this.competenciasComportamentaisOrdenadas.map(
              (c) => this.mCompetenciaAssociadoQtdComportamento.get(c.nome) || 0,
            ),
            color: '#2D5269',
          },
        ],
        categorias: this.competenciasComportamentaisOrdenadas.map((c) => c.nome),
        tooltip: {
          headerFormat: '<span style="padding:2px;"><b>{point.key}</b></span><table>',
          pointFormat:
            '<tr>'
            + '<td style="color:{series.color};padding:0">Comportamentos Associados: </td>'
            + '<td style="padding:2px"><span>{point.y}</span></td>'
            + '</tr>',
          footerFormat: '</table>',
          hideDelay: 100,
          shared: true,
          useHTML: true,
        },
      };
    },

    dadosPiechartAssociados() {
      return {
        dados: [
          {
            name: 'Competências',
            data: _.sortBy(
              this.competenciasComportamentaisOrdenadas.map((cc) => ({
                name: cc.nome,
                y: this.mCompetenciaAssociadoQtdComportamento.get(cc.nome) || 0,
              })),
              (d) => -1 * d.y,
            ),
          },
        ],
      };
    },
  },

  methods: {
    async getRelatorio() {
      const documento = {
        nome: 'relatorio-monitoramento-mapeamento-unidade-competencia-comportamental',
        orientacao: 'landscape',
        cabecalho: {
          titulo: 'Relatório do Mapeamento Comportamental por Unidade',
          itens: [
            'Tipo de monitoramento: Competências Comportamentais',
            `Unidade: ${this.unidadeSelecionada.sigla}`,
            `Mapeamento: ${this.mapeamento.nome}`,
          ],
        },
        blocos: [
          {
            titulo: 'GRÁFICOS',
            subtitulo: 'MONITORAMENTO DO CADASTRO DE COMPETÊNCIAS COMPORTAMENTAIS',
            itens: [
              { tipo: 'imagem', valor: await getCanvas('#grafico-barras') },
            ],
          },
          {
            titulo: '',
            subtitulo: 'COMPARATIVO DE COMPETÊNCIAS COMPORTAMENTAIS',
            itens: [
              { tipo: 'imagem', valor: await getCanvas('#grafico-pizza') },
            ],
          },
          {
            titulo: 'RESULTADOS',
            subtitulo: '',
            itens: [{
              tipo: 'tabela',
              valor: {
                colunas: [
                  ...this.dadosTabela.headers.map((h) => (
                    h.text
                  )),
                ],
                linhas: this.dadosTabela.itemsPDF,
              },
            }],
          },
        ],
      };
      generatePDF(documento);
    },
  },
};
</script>

<style scoped>
.chart {
  width: 100%;
  max-height: 400px;
  font-family: Roboto, sans-serif;
}

.chart >>> .highcharts-legend-item rect {
  display: none;
}

.chart >>> .highcharts-legend-item {
  pointer-events: none;
}
</style>
